import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const eventPhotos = [
  'https://public.readdy.ai/ai/img_res/c91381d16f300f29a79ad40b8e063cdf.jpg',
  'https://readdy.ai/api/search-image?query=bartender%20hands%20crafting%20signature%20cocktail%20shaking%20silver%20shaker%20dramatic%20motion%20professional%20technique%20warm%20ambient%20lighting%20elegant%20bar%20setting&width=1200&height=675&seq=event-slide-2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=beautiful%20array%20of%20colorful%20signature%20cocktails%20lined%20up%20on%20bar%20counter%20garnished%20with%20fresh%20herbs%20citrus%20edible%20flowers%20professional%20presentation&width=1200&height=675&seq=event-slide-3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=elegant%20corporate%20event%20cocktail%20party%20guests%20enjoying%20drinks%20sophisticated%20venue%20warm%20lighting%20professional%20mobile%20bar%20service&width=1200&height=675&seq=event-slide-4&orientation=landscape',
  'https://readdy.ai/api/search-image?query=glamorous%20wedding%20reception%20mobile%20cocktail%20bar%20setup%20outdoor%20garden%20venue%20string%20lights%20romantic%20atmosphere%20professional%20bartender%20serving&width=1200&height=675&seq=event-slide-5&orientation=landscape',
  'https://readdy.ai/api/search-image?query=group%20cocktail%20making%20class%20students%20learning%20mixology%20techniques%20laughing%20having%20fun%20instructor%20demonstrating%20elegant%20venue&width=1200&height=675&seq=event-slide-6&orientation=landscape',
  'https://readdy.ai/api/search-image?query=close%20up%20pouring%20golden%20cocktail%20into%20crystal%20coupe%20glass%20garnish%20placement%20professional%20bartending%20technique%20warm%20lighting&width=1200&height=675&seq=event-slide-7&orientation=landscape',
  'https://readdy.ai/api/search-image?query=celebration%20toast%20champagne%20glasses%20clinking%20golden%20liquid%20bubbles%20rising%20warm%20bokeh%20background%20luxury%20event%20moment&width=1200&height=675&seq=event-slide-8&orientation=landscape',
];

export default function ConceptVideo() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: containerRef, isVisible: videoVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });
  const { ref: featuresRef, isVisible: featuresVisible } = useScrollAnimation();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % eventPhotos.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section id="concept" className="relative py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20luxurious%20cocktail%20bar%20interior%20with%20warm%20golden%20ambient%20lighting%2C%20bokeh%20lights%20reflecting%20off%20crystal%20glasses%20and%20premium%20spirit%20bottles%2C%20elegant%20moody%20atmosphere%20with%20deep%20shadows%20and%20rich%20amber%20tones%2C%20cinematic%20wide%20angle%20photography&width=1920&height=1080&seq=concept-bg-002&orientation=landscape"
          alt="Concept background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/75 to-black/90"></div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-amber-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-400/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-500"></div>
            <span className="text-amber-400 text-xs font-semibold tracking-[0.25em] uppercase">Our Story</span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-500"></div>
          </div>
          <h2 className="text-5xl md:text-6xl font-display font-normal text-white mb-6 leading-tight">
            The <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300">House of FMR</span> Experience
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto leading-relaxed">
            Discover what makes our luxury mixology academy unique. From intimate one-on-one sessions to glamorous celebrations — see how we transform every occasion.
          </p>
        </div>

        {/* Cinematic Slideshow */}
        <div 
          ref={containerRef}
          className={`max-w-5xl mx-auto scroll-animate-scale ${videoVisible ? 'visible' : ''}`}
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10">
            <div className="relative w-full h-[300px] sm:h-[400px] md:h-[520px]">
              {/* Slideshow Images */}
              {eventPhotos.map((photo, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    currentSlide === index ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={photo}
                    alt={`House of FMR Event ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30"></div>

              {/* Slide Counter */}
              <div className="absolute top-6 right-6 px-4 py-2 bg-black/60 backdrop-blur-sm rounded-full text-white/80 text-sm font-medium flex items-center gap-2">
                <i className="ri-image-2-line text-amber-400"></i>
                {currentSlide + 1} / {eventPhotos.length}
              </div>

              {/* Progress Dots */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
                {eventPhotos.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`transition-all duration-300 rounded-full ${
                      currentSlide === index
                        ? 'w-8 h-2 bg-amber-400'
                        : 'w-2 h-2 bg-white/40 hover:bg-white/60'
                    }`}
                  />
                ))}
              </div>

              {/* Title Overlay */}
              <div className="absolute bottom-6 left-6">
                <p className="text-amber-400 text-xs font-semibold tracking-[0.2em] uppercase mb-2">Experience Gallery</p>
                <p className="text-white text-2xl font-display">The Art of Luxury Mixology</p>
              </div>

              {/* Auto-play Indicator */}
              <div className="absolute top-6 left-6 px-4 py-2 bg-black/60 backdrop-blur-sm rounded-full text-white/80 text-sm font-medium flex items-center gap-2">
                <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
                Auto-playing
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mt-8 text-center">
            <p className="text-white/60 text-sm max-w-3xl mx-auto leading-relaxed">
              <i className="ri-information-line mr-2"></i>
              Explore our collection of events, classes, and celebrations. Each moment captured showcases the House of FMR experience.
            </p>
          </div>
        </div>

        {/* Feature Highlights */}
        <div 
          ref={featuresRef}
          className={`grid md:grid-cols-3 gap-6 mt-16 max-w-5xl mx-auto stagger-children ${featuresVisible ? 'visible' : ''}`}
        >
          {[
            {
              icon: 'ri-hand-heart-line',
              title: 'Hands-On Learning',
              desc: 'Every class is interactive — you shake, stir, muddle, and pour with expert guidance.',
            },
            {
              icon: 'ri-home-smile-line',
              title: 'We Come to You',
              desc: 'From your kitchen to your party venue — we bring the full bar experience to your doorstep.',
            },
            {
              icon: 'ri-trophy-line',
              title: 'Walk Away a Mixologist',
              desc: 'Leave every session with new skills, personalised recipes, and confidence to impress.',
            },
          ].map((feature, i) => (
            <div
              key={i}
              className="bg-white/[0.05] backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-amber-500/30 transition-all duration-300 group cursor-default"
            >
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-5 group-hover:bg-gradient-to-br group-hover:from-amber-400 group-hover:to-yellow-300 transition-all">
                <i className={`${feature.icon} text-2xl text-amber-400 group-hover:text-stone-900 transition-colors`}></i>
              </div>
              <h4 className="text-white font-serif text-lg font-semibold mb-3">{feature.title}</h4>
              <p className="text-white/40 text-sm leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}